import type { ReactNode } from 'react';

import { cn } from '@kit/ui/utils';

import type { LandingRenderContext } from './landing-context';

function inline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(/(\*\*[^*\n]+\*\*)/g).filter(Boolean).map((part, index) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={`${keyPrefix}-${index}`}>{part.slice(2, -2)}</strong>
      : part,
  );
}

function lines(text: string, keyPrefix: string) {
  return text.split('\n').flatMap((line, index) => [
    ...(index ? [<br key={`${keyPrefix}-br-${index}`} />] : []),
    ...inline(line, `${keyPrefix}-${index}`),
  ]);
}

const BULLET = /^\s*(?:[-•]\s+)/;
const HEADING = /^(#{2,3})\s+(.+)$/;

type Segment = { kind: 'heading' | 'list' | 'paragraph'; lines: string[]; level?: 2 | 3 };

/** Groups a block's lines: `### ` headings, runs of `- ` bullets, and the paragraph text between them. */
function segments(blockLines: string[]) {
  const result: Segment[] = [];
  for (const line of blockLines) {
    const heading = HEADING.exec(line);
    const kind = heading ? 'heading' : BULLET.test(line) ? 'list' : 'paragraph';
    const text = heading ? heading[2]! : kind === 'list' ? line.replace(BULLET, '') : line;
    const last = result.at(-1);
    if (last && last.kind === kind && kind !== 'heading') last.lines.push(text);
    else result.push({ kind, lines: [text], ...(heading ? { level: heading[1]!.length === 2 ? 2 : 3 } : {}) });
  }
  return result;
}

/**
 * Body copy for landing blocks. Plain sites keep a single pre-line paragraph;
 * with `theme.textFormatting: 'rich'` it renders blank-line paragraphs,
 * `- ` bullet lists, `### ` subheadings and `**bold**` — as React elements,
 * never raw HTML.
 */
export function LandingProse({ text, className, ctx }: { text: string; className?: string; ctx: Pick<LandingRenderContext, 'richText'> }) {
  if (!ctx.richText) return <p className={className}>{text}</p>;

  const blocks = text.replace(/\r\n?/g, '\n').split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
  return (
    <div className={cn(className, 'landing-rich-text !whitespace-normal')}>
      {blocks.flatMap((block, index) => segments(block.split('\n')).map((segment, part) => {
        const key = `block-${index}-${part}`;
        if (segment.kind === 'heading') {
          const Heading = segment.level === 2 ? 'h2' : 'h3';
          return <Heading key={key}>{inline(segment.lines[0]!, key)}</Heading>;
        }
        if (segment.kind === 'list') return <ul key={key}>{segment.lines.map((line, item) => <li key={item}>{inline(line, `${key}-${item}`)}</li>)}</ul>;
        return <p key={key}>{lines(segment.lines.join('\n'), key)}</p>;
      }))}
    </div>
  );
}
